import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { useI18n } from "@/i18n";

// Real attack-origin cities (lat, lng) with flag + typical attack vector.
export const SOURCES = [
  { city: "Moscova", country: "Rusia", country_en: "Russia", code: "RU", flag: "🇷🇺", lat: 55.75, lng: 37.62, atk: "SSH brute-force", ip: "5.188.206.74", isp: "Rostelecom" },
  { city: "Sankt Petersburg", country: "Rusia", country_en: "Russia", code: "RU", flag: "🇷🇺", lat: 59.93, lng: 30.34, atk: "Port scan", ip: "46.161.27.113", isp: "Selectel" },
  { city: "Shanghai", country: "China", country_en: "China", code: "CN", flag: "🇨🇳", lat: 31.23, lng: 121.47, atk: "Exploit attempt", ip: "116.31.116.28", isp: "China Telecom" },
  { city: "Beijing", country: "China", country_en: "China", code: "CN", flag: "🇨🇳", lat: 39.9, lng: 116.4, atk: "RDP brute-force", ip: "123.125.71.19", isp: "China Unicom" },
  { city: "Hong Kong", country: "Hong Kong", country_en: "Hong Kong", code: "HK", flag: "🇭🇰", lat: 22.32, lng: 114.17, atk: "Botnet C2", ip: "103.98.212.45", isp: "HKT Limited" },
  { city: "São Paulo", country: "Brazilia", country_en: "Brazil", code: "BR", flag: "🇧🇷", lat: -23.55, lng: -46.63, atk: "Credential stuffing", ip: "177.67.82.201", isp: "Vivo · Telefônica" },
  { city: "Mumbai", country: "India", country_en: "India", code: "IN", flag: "🇮🇳", lat: 19.07, lng: 72.87, atk: "SQL injection", ip: "103.21.244.17", isp: "Reliance Jio" },
  { city: "Lagos", country: "Nigeria", country_en: "Nigeria", code: "NG", flag: "🇳🇬", lat: 6.52, lng: 3.37, atk: "Phishing relay", ip: "197.210.55.98", isp: "MTN Nigeria" },
  { city: "Amsterdam", country: "Olanda", country_en: "Netherlands", code: "NL", flag: "🇳🇱", lat: 52.37, lng: 4.9, atk: "VPN exit scan", ip: "185.220.101.34", isp: "M247 Europe" },
  { city: "Seoul", country: "Coreea de Sud", country_en: "South Korea", code: "KR", flag: "🇰🇷", lat: 37.56, lng: 126.97, atk: "DDoS SYN flood", ip: "175.223.14.207", isp: "SK Telecom" },
  { city: "Istanbul", country: "Turcia", country_en: "Turkey", code: "TR", flag: "🇹🇷", lat: 41.01, lng: 28.97, atk: "Web fuzzing", ip: "88.255.19.142", isp: "Türk Telekom" },
  { city: "Teheran", country: "Iran", country_en: "Iran", code: "IR", flag: "🇮🇷", lat: 35.68, lng: 51.38, atk: "CVE probe", ip: "2.144.71.63", isp: "Telecom Iran · TIC" },
  { city: "Hanoi", country: "Vietnam", country_en: "Vietnam", code: "VN", flag: "🇻🇳", lat: 21.02, lng: 105.83, atk: "SMB exploit", ip: "113.161.88.29", isp: "VNPT" },
  { city: "Kyiv", country: "Ucraina", country_en: "Ukraine", code: "UA", flag: "🇺🇦", lat: 50.45, lng: 30.52, atk: "Port scan", ip: "178.150.202.75", isp: "Kyivstar" },
];

const HQ = { lat: 44.43, lng: 26.1 }; // Bucureşti

function latLngToVec3(lat, lng, r) {
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (lng + 180) * (Math.PI / 180);
  return new THREE.Vector3(
    -r * Math.sin(phi) * Math.cos(theta),
    r * Math.cos(phi),
    r * Math.sin(phi) * Math.sin(theta)
  );
}

function fib(n, r) {
  const pts = [];
  const phi = Math.PI * (3 - Math.sqrt(5));
  for (let i = 0; i < n; i++) {
    const y = 1 - (i / (n - 1)) * 2;
    const rad = Math.sqrt(1 - y * y);
    const theta = phi * i;
    pts.push(new THREE.Vector3(Math.cos(theta) * rad, y, Math.sin(theta) * rad).multiplyScalar(r));
  }
  return pts;
}

export const Globe = ({ tour = false, onTourChange }) => {
  const { lang, t } = useI18n();
  const mountRef = useRef(null);
  const tourRef = useRef(tour);
  const cbRef = useRef(onTourChange);
  const [tip, setTip] = useState(null); // {x,y,info}

  useEffect(() => { tourRef.current = tour; }, [tour]);
  useEffect(() => { cbRef.current = onTourChange; }, [onTourChange]);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    const R = 1.5;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 100);
    camera.position.set(0, 0.4, 4.4);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    mount.appendChild(renderer.domElement);
    Object.assign(renderer.domElement.style, { width: "100%", height: "100%", display: "block" });

    const group = new THREE.Group();
    group.rotation.x = 0.35;
    scene.add(group);

    const inner = new THREE.Mesh(
      new THREE.SphereGeometry(R * 0.985, 48, 48),
      new THREE.MeshBasicMaterial({ color: 0x111a26 })
    );
    group.add(inner);
    const wire = new THREE.Mesh(
      new THREE.SphereGeometry(R, 44, 44),
      new THREE.MeshBasicMaterial({ color: 0x1c6274, wireframe: true, transparent: true, opacity: 0.3 })
    );
    group.add(wire);
    const pg = new THREE.BufferGeometry().setFromPoints(fib(520, R * 1.008));
    group.add(new THREE.Points(pg, new THREE.PointsMaterial({ color: 0x00e5ff, size: 0.02, transparent: true, opacity: 0.65 })));
    group.add(new THREE.Mesh(
      new THREE.SphereGeometry(R * 1.18, 48, 48),
      new THREE.MeshBasicMaterial({ color: 0x00e5ff, transparent: true, opacity: 0.06, side: THREE.BackSide })
    ));

    // HQ node
    const target = latLngToVec3(HQ.lat, HQ.lng, R * 1.01);
    const hq = new THREE.Mesh(new THREE.SphereGeometry(0.05, 16, 16), new THREE.MeshBasicMaterial({ color: 0x00e5ff }));
    hq.position.copy(target);
    group.add(hq);
    const hqRing = new THREE.Mesh(
      new THREE.RingGeometry(0.07, 0.085, 32),
      new THREE.MeshBasicMaterial({ color: 0x00e5ff, transparent: true, opacity: 0.5, side: THREE.DoubleSide })
    );
    hqRing.position.copy(target);
    hqRing.lookAt(new THREE.Vector3(0, 0, 0));
    group.add(hqRing);

    // Source nodes + raycast hit spheres
    const RED = 0xff3b6b;
    const srcNodes = [];
    SOURCES.forEach((s, i) => {
      const pos = latLngToVec3(s.lat, s.lng, R * 1.01);
      const dot = new THREE.Mesh(
        new THREE.SphereGeometry(0.035, 12, 12),
        new THREE.MeshBasicMaterial({ color: RED, transparent: true, blending: THREE.AdditiveBlending })
      );
      dot.position.copy(pos);
      group.add(dot);
      const hit = new THREE.Mesh(
        new THREE.SphereGeometry(0.13, 8, 8),
        new THREE.MeshBasicMaterial({ visible: false })
      );
      hit.position.copy(pos);
      hit.userData = { info: s, dot };
      group.add(hit);
      srcNodes.push(hit);
    });

    // Arcs pool
    const ARCS = 14;
    const arcs = [];
    const CYAN = new THREE.Color(0x00e5ff);
    const VIOLET = new THREE.Color(0x7c5cfc);
    const REDC = new THREE.Color(RED);
    function spawn(a) {
      const s = SOURCES[Math.floor(Math.random() * SOURCES.length)];
      const start = latLngToVec3(s.lat, s.lng, R * 1.01);
      const mid = start.clone().add(target).multiplyScalar(0.5);
      const lift = 1 + start.distanceTo(target) * 0.45;
      mid.normalize().multiplyScalar(R * lift);
      const curve = new THREE.QuadraticBezierCurve3(start, mid, target);
      if (a) { a.curve = curve; a.line.geometry.setFromPoints(curve.getPoints(60)); a.t = 0; return; }
      const geo = new THREE.BufferGeometry().setFromPoints(curve.getPoints(60));
      const roll = Math.random();
      const col = roll < 0.55 ? REDC : roll < 0.82 ? CYAN : VIOLET;
      const line = new THREE.Line(geo, new THREE.LineBasicMaterial({ color: col, transparent: true, opacity: 0, blending: THREE.AdditiveBlending }));
      const head = new THREE.Mesh(new THREE.SphereGeometry(0.03, 10, 10), new THREE.MeshBasicMaterial({ color: col, transparent: true, blending: THREE.AdditiveBlending }));
      group.add(line); group.add(head);
      return { line, head, curve, t: Math.random(), speed: 0.004 + Math.random() * 0.006 };
    }
    for (let i = 0; i < ARCS; i++) arcs.push(spawn());

    // Raycaster hover
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();
    const hoveredRef = { node: null };
    const tmp = new THREE.Vector3();

    function projectTip(node) {
      if (!node) return;
      const w = node.getWorldPosition(tmp).clone().project(camera);
      const rect = renderer.domElement.getBoundingClientRect();
      const x = (w.x * 0.5 + 0.5) * rect.width;
      const y = (-w.y * 0.5 + 0.5) * rect.height;
      setTip({ x, y, info: node.userData.info });
    }
    function onMove(e) {
      const rect = renderer.domElement.getBoundingClientRect();
      mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(mouse, camera);
      const hits = raycaster.intersectObjects(srcNodes, false);
      if (hits.length) {
        const node = hits[0].object;
        if (hoveredRef.node !== node) {
          if (hoveredRef.node) hoveredRef.node.userData.dot.scale.setScalar(1);
          hoveredRef.node = node;
          node.userData.dot.scale.setScalar(1.8);
          renderer.domElement.style.cursor = "pointer";
        }
        projectTip(node);
      } else if (hoveredRef.node) {
        hoveredRef.node.userData.dot.scale.setScalar(1);
        hoveredRef.node = null;
        renderer.domElement.style.cursor = "grab";
        setTip(null);
      }
    }
    function onLeave() {
      if (hoveredRef.node) hoveredRef.node.userData.dot.scale.setScalar(1);
      hoveredRef.node = null;
      setTip(null);
    }
    renderer.domElement.style.cursor = "grab";
    renderer.domElement.addEventListener("pointermove", onMove);
    renderer.domElement.addEventListener("pointerleave", onLeave);

    function resize() {
      const w = mount.clientWidth || 1;
      const h = mount.clientHeight || 1;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    }
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(mount);

    let raf, frame = 0;
    let tourNode = null, tourIdx = -1, tourTimer = 0, tourTargetY = 0, tipAccum = 0;
    const clock = new THREE.Clock();
    function animate() {
      const dt = clock.getDelta();
      frame += 1;

      // Rotation + auto-tour (mod prezentare)
      if (tourRef.current) {
        tourTimer += dt;
        if (!tourNode || tourTimer > 3.4) {
          tourTimer = 0;
          if (tourNode) tourNode.userData.dot.scale.setScalar(1);
          tourIdx = (tourIdx + 1) % srcNodes.length;
          tourNode = srcNodes[tourIdx];
          const v = tourNode.position;
          tourTargetY = -Math.atan2(v.x, v.z);
          if (cbRef.current) cbRef.current(tourNode.userData.info);
        }
        if (tourNode) tourNode.userData.dot.scale.setScalar(2.4);
        if (!hoveredRef.node) {
          let diff = tourTargetY - group.rotation.y;
          diff = Math.atan2(Math.sin(diff), Math.cos(diff));
          group.rotation.y += diff * Math.min(1, dt * 2.4);
        }
      } else {
        if (tourNode) { tourNode.userData.dot.scale.setScalar(1); tourNode = null; if (cbRef.current) cbRef.current(null); }
        if (!hoveredRef.node) group.rotation.y += 0.12 * dt;
      }

      const p = 1 + Math.sin(frame * 0.05) * 0.25;
      hq.scale.setScalar(p);
      hqRing.scale.setScalar(1 + Math.sin(frame * 0.05) * 0.4);
      hqRing.material.opacity = 0.35 + Math.sin(frame * 0.05) * 0.2;
      for (const a of arcs) {
        a.t += a.speed;
        if (a.t >= 1.15) spawn(a);
        const tt = Math.min(a.t, 1);
        a.head.position.copy(a.curve.getPoint(tt));
        const op = Math.sin(Math.min(a.t, 1) * Math.PI);
        a.line.material.opacity = op * 0.85;
        a.head.material.opacity = op;
        a.head.visible = a.t < 1;
      }

      // Tooltip (throttled): hover has priority, else the toured city
      tipAccum += dt;
      if (tipAccum > 0.05) {
        tipAccum = 0;
        const node = hoveredRef.node || (tourRef.current ? tourNode : null);
        if (node) projectTip(node);
        else if (!hoveredRef.node) setTip(null);
      }
      renderer.render(scene, camera);
      raf = requestAnimationFrame(animate);
    }
    animate();

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      renderer.domElement.removeEventListener("pointermove", onMove);
      renderer.domElement.removeEventListener("pointerleave", onLeave);
      renderer.dispose();
      scene.traverse((o) => {
        if (o.geometry) o.geometry.dispose();
        if (o.material) Array.isArray(o.material) ? o.material.forEach((m) => m.dispose()) : o.material.dispose();
      });
      if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
    };
  }, []);

  return (
    <div ref={mountRef} className="relative h-full w-full" data-testid="globe-canvas">
      {tip && (
        <div
          data-testid="globe-tooltip"
          className="pointer-events-none absolute z-20 -translate-x-1/2 -translate-y-[calc(100%+14px)] whitespace-nowrap rounded-lg border border-[#ff3b6b]/40 bg-ink-900/95 px-3.5 py-2.5 shadow-[0_8px_30px_rgba(0,0,0,0.5)] backdrop-blur-md"
          style={{ left: tip.x, top: tip.y }}
        >
          <div className="flex items-center gap-2">
            <span className="text-lg leading-none">{tip.info.flag}</span>
            <div>
              <div className="font-display text-sm font-bold leading-tight text-white">
                {tip.info.city}, {tip.info.code}
              </div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-white/45">{lang === "en" ? tip.info.country_en : tip.info.country}</div>
            </div>
          </div>
          <div className="mt-2 flex items-center justify-between gap-4 border-t border-white/10 pt-2">
            <div>
              <div className="font-mono text-[9px] uppercase tracking-widest text-white/35">{lang === "en" ? "Source IP" : "Sursă IP"}</div>
              <div className="font-mono text-[12px] text-cyan-neon">{tip.info.ip}</div>
            </div>
            <div className="text-right">
              <div className="font-mono text-[9px] uppercase tracking-widest text-white/35">ISP</div>
              <div className="font-mono text-[11px] text-white/70">{tip.info.isp}</div>
            </div>
          </div>
          <div className="mt-2 flex items-center gap-1.5 border-t border-white/10 pt-2">
            <span className="h-1.5 w-1.5 rounded-full bg-[#ff3b6b] shadow-[0_0_8px_#ff3b6b]" />
            <span className="font-mono text-[10px] text-[#ff7a97]">{tip.info.atk}</span>
            <span className="ml-1 rounded bg-emerald-400/15 px-1.5 py-0.5 font-mono text-[9px] font-bold text-emerald-300">{t("am.blocked")}</span>
          </div>
        </div>
      )}
    </div>
  );
};
